import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import CustomSelect from "components/CustomSelect";
import GoalButton from "layouts/goals/components/GoalButton";
import goalSelectStyles from "layouts/goals/components/goalSelectStyles";
import FieldLabel from "./FieldLabel";
import { ASSETS_MESSAGES, personName, typeOf } from "../assets.constants";

const todayStr = () => moment().format("YYYY-MM-DD");

// Gives a company asset to an employee. Either one can be fixed by the caller
// (assigning from a row, or from an employee), the other is chosen here.
function AssignAssetModal({
  open,
  onClose,
  onAssign,
  isSubmitting,
  asset,
  employee,
  spareAssets,
  assignees,
}) {
  const [assetId, setAssetId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [date, setDate] = useState(todayStr());
  const [notes, setNotes] = useState("");
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    setAssetId(asset?.id ?? null);
    setUserId(employee?.user_slack_id ?? null);
    setDate(todayStr());
    setNotes("");
    setLocalError(null);
  }, [open, asset, employee]);

  const assetOptions = useMemo(
    () =>
      spareAssets.map((item) => ({
        value: item.id,
        label: `${item.name} (${typeOf(item.type)?.label}${
          item.serial_number ? `, ${item.serial_number}` : ""
        })`,
      })),
    [spareAssets]
  );
  const employeeOptions = useMemo(
    () =>
      [...assignees]
        // Active people first, so a duplicate account that was archived sits below.
        .sort((a, b) => Number(b.is_tracked !== false) - Number(a.is_tracked !== false))
        .map((person) => {
          // Exited wins over archived; active, tracked people get no tag.
          let state = "";
          if (person.employment_status === "exited") state = " (Exited)";
          else if (person.is_tracked === false) state = " (Archived)";
          return {
            value: person.user_slack_id,
            label: `${personName(person)}${
              person.employee_id ? ` (${person.employee_id})` : ""
            }${state}`,
          };
        }),
    [assignees]
  );

  const handleAssign = async () => {
    if (!assetId) return setLocalError(ASSETS_MESSAGES.assetRequired);
    if (!userId) return setLocalError(ASSETS_MESSAGES.employeeRequired);
    setLocalError(null);
    await onAssign(assetId, { user_id: userId, assigned_at: date || todayStr(), notes });
  };

  return (
    <Dialog
      open={open}
      onClose={() => !isSubmitting && onClose()}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { overflow: "visible" } }}
    >
      <DialogTitle>Assign asset</DialogTitle>
      <DialogContent sx={{ overflow: "visible" }}>
        {localError && (
          <SuiTypography variant="caption" color="error" display="block" mb={1}>
            {localError}
          </SuiTypography>
        )}
        <SuiBox mb={2}>
          <FieldLabel>Asset</FieldLabel>
          <CustomSelect
            options={assetOptions}
            value={assetOptions.find((option) => option.value === assetId) || null}
            onChange={(option) => setAssetId(option.value)}
            customStyles={goalSelectStyles}
            menuPortalTarget={document.body}
            isSearchable
            isDisabled={!!asset}
            placeholder="Select an asset in stock"
            noOptionsMessage={() => "No assets in stock"}
          />
        </SuiBox>
        <SuiBox mb={2}>
          <FieldLabel>Employee</FieldLabel>
          <CustomSelect
            options={employeeOptions}
            value={employeeOptions.find((option) => option.value === userId) || null}
            onChange={(option) => setUserId(option.value)}
            customStyles={goalSelectStyles}
            menuPortalTarget={document.body}
            isSearchable
            isDisabled={!!employee}
            placeholder="Select employee"
          />
        </SuiBox>
        <SuiBox mb={2}>
          <FieldLabel>Assigned on</FieldLabel>
          <SuiInput type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </SuiBox>
        <FieldLabel>Notes</FieldLabel>
        <SuiInput
          multiline
          rows={2}
          placeholder="Optional"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <GoalButton variant="secondary" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </GoalButton>
        <GoalButton
          variant="primary"
          icon="person_add"
          loading={isSubmitting}
          onClick={handleAssign}
        >
          Assign
        </GoalButton>
      </DialogActions>
    </Dialog>
  );
}

export default AssignAssetModal;
